import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminAPI } from '../services/adminAPI';
import { shipmentsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import ShipmentDetailModal from '../components/ShipmentDetailModal';
import './AllShipments.css';

function AllShipments() {
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [filters, setFilters] = useState({
    search: '',
    user_id: '',
    status: searchParams.get('status') || '',
    type: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    fetchShipments();
  }, [page]);

  // Fetch on mount
  useEffect(() => {
    fetchShipments();
  }, []);

  const fetchShipments = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await adminAPI.getAllShipments({
        page,
        per_page: 20,
        ...filters,
      });

      setShipments(response.data.shipments);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (err) {
      setError('Failed to load shipments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchShipments();
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      user_id: '',
      status: '',
      type: '',
      date_from: '',
      date_to: '',
    });
    setPage(1);
    setTimeout(() => fetchShipments(), 0);
  };

  const handleViewShipment = async (shipment) => {
    try {
      const response = await shipmentsAPI.getById(shipment.id);
      setSelectedShipment(response.data.shipment);
      setShowDetailModal(true);
    } catch (err) {
      setError('Failed to load shipment details');
      console.error(err);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedShipment(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  return (
    <div className="app-container">
      <Header />
      <Navigation activeTab="admin" />

      <div className="content">
        <div className="page-header">
          <h1 className="page-title">All Shipments</h1>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label>Search</label>
              <input
                type="text"
                name="search"
                placeholder="Shipper, consignee, PO, reference..."
                value={filters.search}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>User ID</label>
              <input
                type="text"
                name="user_id"
                placeholder="Filter by user..."
                value={filters.user_id}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">All Statuses</option>
                <option value="submitted">Submitted</option>
                <option value="rejected">Rejected</option>
                <option value="resubmitted">Resubmitted</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="in_transit">In Transit</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Type</label>
              <select
                name="type"
                value={filters.type}
                onChange={handleFilterChange}
                className="filter-select"
              >
                <option value="">All Types</option>
                <option value="domestic">Domestic</option>
                <option value="international">International</option>
              </select>
            </div>
          </div>

          <div className="filters-row">
            <div className="filter-group">
              <label>Date From</label>
              <input
                type="date"
                name="date_from"
                value={filters.date_from}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Date To</label>
              <input
                type="date"
                name="date_to"
                value={filters.date_to}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>

            <div className="filter-actions">
              <button className="btn-primary" onClick={handleApplyFilters}>
                Apply Filters
              </button>
              <button className="btn-clear" onClick={handleClearFilters}>
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="stats-bar">
          <div className="stat-item">
            <span className="label">Total Shipments</span>
            <span className="value">{total}</span>
          </div>
          <div className="stat-item">
            <span className="label">Current Page</span>
            <span className="value">
              {page} / {totalPages}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading shipments...</div>
        ) : shipments.length === 0 ? (
          <div className="empty-state">No shipments found</div>
        ) : (
          <>
            <div className="table-container">
              <table className="shipments-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Type</th>
                    <th>Shipper</th>
                    <th>Consignee</th>
                    <th>Route</th>
                    <th>Status</th>
                    <th>PO Number</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading shipments...</p>
                      </td>
                    </tr>
                  ) : shipments.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="empty-state">
                        <div className="empty-state-content">
                          <p className="empty-state-title">No shipments found</p>
                          <p className="empty-state-text">
                            {filters.search || filters.user || filters.status || filters.type
                              ? 'Try adjusting your filters'
                              : 'No shipments have been created yet'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    shipments.map((shipment) => (
                      <tr key={shipment.id}>
                        <td>#{shipment.id}</td>
                        <td>
                          <div className="user-info">
                            <span className="user-name">{shipment.user_name}</span>
                            <span className="user-id">{shipment.username}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`badge-type ${shipment.shipment_type}`}>
                            {shipment.shipment_type}
                          </span>
                        </td>
                        <td>
                          <div className="location-info">
                            <span>{shipment.shipper_name}</span>
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                              {shipment.shipper_city}, {shipment.shipper_state}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="location-info">
                            <span>{shipment.consignee_name}</span>
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                              {shipment.consignee_city}, {shipment.consignee_state}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span style={{ fontSize: '0.9rem' }}>
                            {shipment.shipper_city}, {shipment.shipper_state}
                            <span className="location-arrow">→</span>
                            {shipment.consignee_city}, {shipment.consignee_state}
                          </span>
                        </td>
                        <td>
                          <span className={`badge-status ${shipment.status === 'rejected' ? 'status-badge-rejected' : shipment.status === 'resubmitted' ? 'status-badge-resubmitted' : shipment.status === 'approved' ? 'status-badge-approved' : shipment.status}`}>
                            {shipment.status === 'approved' ? 'Approved' : shipment.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td>{shipment.purchase_order || '-'}</td>
                        <td>{formatTimeAgo(shipment.created_at)}</td>
                        <td>
                          <button className="btn-view" onClick={() => handleViewShipment(shipment)}>
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="btn-page"
                >
                  Previous
                </button>
                <span className="page-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="btn-page"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Shipment Detail Modal */}
        {showDetailModal && selectedShipment && (
          <ShipmentDetailModal
            shipment={selectedShipment}
            onClose={closeDetailModal}
            onClone={() => {}}
            showCloneButton={false}
            showDeleteButton={currentUser?.role === 'super_admin'}
            onDelete={() => {}}
          />
        )}
      </div>
    </div>
  );
}

export default AllShipments;
